<script setup>
const props = defineProps({
    image: {
        type: Object,
        required: false,
        default: {},
    },
    src: {
        type: String,
        required: false,
        default: null,
    },
    // Optional art-direction override for narrow viewports. Same shape as
    // `image` (`{ url | src, alt }`) or pass `mobileSrc` for a plain string.
    // When provided, the rendered <picture> swaps to this asset under
    // `(max-width: 767px)` so portrait/mobile crops can be authored without
    // losing the proxy/transform pipeline on desktop.
    mobileImage: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    mobileSrc: {
        type: String,
        required: false,
        default: null,
    },
    alt: {
        type: String,
        required: false,
        default: null,
    },
    lazy: {
        type: Boolean,
        required: false,
        default: true,
    },
    quality: {
        type: Number,
        required: false,
        default: 50,
    },
    forceLoad: {
        type: Boolean,
        required: false,
        default: false,
    },
    height: {
        type: [Number, Boolean],
        required: false,
        default: false,
    },
    width: {
        type: [Number, Boolean],
        required: false,
        default: false,
    },
    mod: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    sizes: {
        type: String,
        required: false,
        default: 'xs:319px sm:639px md:1023px lg:1279px xl:1439 xxl:1920px',
    },
    decoration: {
        type: Boolean,
        required: false,
        default: false,
    }
});

const loadingValue = props.lazy ? 'lazy' : 'eager';

const config = useRuntimeConfig();
const { imageUrlsToRemove } = config.public;
const urlsToRemove = (imageUrlsToRemove || '').split(',').filter(Boolean);
const urlsToProxy = ['v5.airtableusercontent.com', 'cdn.builder.io', 'inbound.com'];
const proxyUrl = 'images.proxy.thisismess.io';

// Run a raw image URL through the same remove/proxy pipeline used for the
// primary image so art-directed mobile sources land on the same CDN.
function normalizeUrl(raw) {
    if (!raw) return null;
    let out = raw;
    if (urlsToRemove.some((u) => out.includes(u))) {
        urlsToRemove.forEach((u) => { out = out.replace(u, ''); });
    }
    if (urlsToProxy.some((u) => out.includes(u))) {
        urlsToProxy.forEach((u) => { out = out.replace(u, proxyUrl); });
    }
    return out;
}

const url = normalizeUrl(props.image?.url || props.image?.src || props.src);
const mobileUrl = normalizeUrl(
    props.mobileImage?.url || props.mobileImage?.src || props.mobileSrc
);

const maxWidth = 1920;
let setWidth = props.width * 2 || maxWidth || null;
if (setWidth && setWidth > maxWidth) setWidth = maxWidth;

const baseModifier = {
    w: setWidth,
    q: props.quality,
    h: props.height || null,
    fit: 'max',
    fm: 'webp',
    fo: 'face',
    ...props.mod,
};
</script>

<template>
    <div v-if="url" class="mess-image">
        <!-- Art-directed: mobile <source> swaps under 768px, desktop falls
             through to the proxy-optimized nuxt-img. -->
        <picture v-if="mobileUrl">
            <source media="(max-width: 767px)" :srcset="mobileUrl" />
            <nuxt-img :src="url" :loading="loadingValue" :preload="!lazy"
                :placeholder="lazy ? 25 : false"
                :alt="!decoration && (alt || image.alt) || ''"
                format="webp" :sizes="sizes || null" :modifiers="{ ...baseModifier }" />
        </picture>
        <nuxt-picture v-else :loading="loadingValue" :preload="!lazy"
            :placeholder="lazy ? 25 : false" :src="url"
            :alt="!decoration && (alt || image.alt) || ''"
            format="webp" :sizes="sizes || null" :modifiers="{ ...baseModifier }" />
    </div>
</template>

<style>
.mess-image {
    position: relative;
}

.mess-image picture {
    display: block;
    width: 100%;
    height: 100%;
}

.mess-image picture.full-sized-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
}

.mess-image picture img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>
